import { useEffect } from 'react';
import { Center, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FaComments } from 'react-icons/fa';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { usePageTextContent } from 'context/pageTextContent';
import { Button, Input, Textarea } from 'components';
import { createNetworkRegisterContact } from 'services/client/repositories/contact';

const validationSchema = z.object({
  email: z.string().email().nonempty(),
  name: z.string(),
  phone: z.string(),
  city: z.string(),
  comment: z.string(),
});

type RegisterNetworkForm = z.infer<typeof validationSchema>;

export const RegisterNetworkCard = () => {
  const { content } = usePageTextContent<RegisterNetworkPageContent>();

  const { introduction } = content;

  useEffect(() => {
    validationSchema.extend({
      email: z
        .string()
        .email(introduction.invalidEmail)
        .nonempty(introduction.invalidEmail),
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm<RegisterNetworkForm>({
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: RegisterNetworkForm) => {
    const response = await createNetworkRegisterContact(data);

    if (response.success) reset();
  };

  return (
    <VStack
      p="1.25rem"
      pt={0}
      rounded="md"
      shadow="xl"
      bg="white"
      w="full"
      spacing="1rem"
    >
      <HStack spacing="1rem" w="full">
        <Center
          bg="network.secondary"
          mt="-1.25rem"
          rounded="sm"
          shadow="xl"
          h="4rem"
          w="4rem"
        >
          <Icon as={FaComments} color="white" fontSize="1.25rem" />
        </Center>

        <Text fontWeight={300}>{introduction.formTitle}</Text>
      </HStack>

      <VStack
        w="full"
        spacing="1.25rem"
        pt="0.5rem"
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Input
          label={introduction.email}
          isRequired
          {...register('email')}
          error={errors.email?.message}
        />

        <Input label={introduction.name} {...register('name')} />

        <Input label={introduction.phone} {...register('phone')} />

        <Input label={introduction.city} {...register('city')} />

        <Textarea label={introduction.comment} {...register('comment')} />

        <HStack w="full" justify="flex-end" spacing="1.25rem">
          {isSubmitSuccessful && (
            <Text color="green.500" fontWeight="bold" fontSize="1rem">
              {introduction.successMessage}
            </Text>
          )}

          <Button
            bg="network.secondary"
            _hover={{ bg: 'network.secondary' }}
            _active={{ bg: 'network.secondary' }}
            rounded="sm"
            fontSize="0.75rem"
            type="submit"
            isLoading={isSubmitting}
          >
            {introduction.submitButton}
          </Button>
        </HStack>
      </VStack>
    </VStack>
  );
};
